import { useContext, useEffect, useState } from 'react';
import { Api } from '@sodamnfoolish/sjc-backend-types';
import { NavLink, useMatch } from 'react-router-dom';

import { useBackend } from '../Hooks/UseBackend';
import { AppMode } from '../types';

import { FeedIcon } from './icons/FeedIcon';
import { ColumnsIcon } from './icons/ColumnsIcon';
import { PlusIcon } from './icons/PlusIcon';
import { EthereumIcon } from './icons/EthereumIcon';
import { SettingsIcon } from './icons/SettingsIcon';
import { Input, Switch } from './form';
import { WalletIcon } from './icons/WalletIcon';
import { SolanaCoinsIcon, SolanaIcon } from './icons/SolanaIcon';
import { SolPriceContext } from './SolPriceProvider';
import { Tooltip } from './Tooltip';

import logoutIcon from './assets/logout.png';

interface NavbarProps {
    user: Api.Auth.User;
    mode: AppMode;
    onFeedDisplayClick: () => void;
    onModeChange: (mode: AppMode) => void;
    getEthWalletStatus: () => Promise<{ address: string; balance: number }>;
    getSolWalletStatus: () => Promise<{ address: string; balance: number }>;
}

export function Navbar({ user, mode, getEthWalletStatus, getSolWalletStatus, onFeedDisplayClick, onModeChange }: NavbarProps) {
    const solPriceUsd = useContext(SolPriceContext);
    const [solBalance, setSolBalance] = useState<number>(0);
    const [ethBalance, setEthBalance] = useState<number>(0);

    const { logout } = useBackend();

    useEffect(() => {
        const fetchEthStatus = () => {
            getEthWalletStatus()
                .then((status) => {
                    console.log('ETH Wallet Status:', status);
                    setEthBalance(status.balance);
                })
                .catch((error) => {
                    //in case if rpc is offline
                    console.error('Error fetching ETH wallet status:', error);
                });
        };
        fetchEthStatus();
        const intervalId = setInterval(fetchEthStatus, 10000);

        return () => clearInterval(intervalId);
    }, [getEthWalletStatus]);

    useEffect(() => {
        const fetchSolStatus = () => {
            getSolWalletStatus()
                .then((status) => {
                    console.log('SOL Wallet Status:', status);
                    setSolBalance(status.balance);
                })
                .catch((error) => {
                    console.error('Error fetching SOL wallet status:', error);
                });
        };

        fetchSolStatus();
        const intervalId = setInterval(fetchSolStatus, 10000);

        return () => clearInterval(intervalId);
    }, [getSolWalletStatus]);

    const isFeed = useMatch('/');

    return (
        <nav className='sticky z-999 left-0 h-[33px] w-screen flex justify-between items-center bg-night-black py-[3px] px-[8px] text-[12px]'>
            <ul className='flex gap-[5px]'>
                <Tooltip text='Monitor Feed'>
                    <li className='h-full w-full'>
                        <NavLink to='/'>
                            {({ isActive }) => <FeedIcon className={`size-[24px] ${isActive ? 'filter-black-to-blue' : 'filter invert'}`} />}
                        </NavLink>
                    </li>
                </Tooltip>
                {isFeed && (
                    <Tooltip text='Customize Feed'>
                        <li>
                            <div onClick={onFeedDisplayClick}>
                                <ColumnsIcon className='size-[24px] filter invert' />
                            </div>
                        </li>
                    </Tooltip>
                )}
                <Tooltip text='Token Create'>
                    <li>
                        <NavLink className='nav-token-create nav-container' to='/TokenCreate'>
                            {({ isActive }) => <PlusIcon className={`size-[24px] ${isActive ? 'filter-white-to-blue' : ''}`} />}
                        </NavLink>
                    </li>
                </Tooltip>
                <Tooltip text='ERCToken Create'>
                    <li>
                        <NavLink className='nav-erc-token-create nav-container' to='/ErcTokenCreate'>
                            {({ isActive }) => <EthereumIcon className={`size-[24px] ${isActive ? 'filter-black-to-blue' : 'filter invert'}`} />}
                        </NavLink>
                    </li>
                </Tooltip>
                <Tooltip text='Solana Token Sell'>
                    <li>
                        <NavLink to='/Result'>
                            {({ isActive }) => <SolanaCoinsIcon className={`size-[24px] ${isActive ? 'filter-black-to-blue' : 'filter invert'}`} />}
                        </NavLink>
                    </li>
                </Tooltip>
                <Tooltip text='Settings'>
                    <li>
                        <NavLink className='nav-settings nav-container' to='/Settings'>
                            {({ isActive }) => <SettingsIcon className={`size-[24px] ${isActive ? 'filter-black-to-blue' : 'filter invert'}`} />}
                        </NavLink>
                    </li>
                </Tooltip>
            </ul>

            <Switch
                rootClassName='ml-[20px] mr-auto'
                checked={mode === 'buy'}
                onChange={(event) => {
                    if (event.target.checked) {
                        onModeChange('buy');
                    } else {
                        onModeChange('deploy');
                    }
                }}
            >
                <span>Deploy</span>
                <span>Buy</span>
            </Switch>

            <div className='flex gap-[12px] items-center'>
                <Input
                    className='w-[162px] !h-[25px]'
                    inputClassName='text-center'
                    icon={<WalletIcon className='size-[20px] filter invert' />}
                    value={`${solBalance?.toFixed(2)} SOL ${ethBalance?.toFixed(2)} ETH`}
                />
                <SolanaIcon className='size-[20px]' />
                <span className='text-white'>{solPriceUsd ? `$${solPriceUsd.toFixed(2)}` : null}</span>
            </div>

            <div className='flex gap-[8px] items-center ml-4'>
                <span className='text-white'>{user.name}</span>
                <img src={user.picture ?? ''} alt='User Avatar' className='w-6 h-6 rounded-full' />
                <button
                    className='btn btn-sm btn-outline'
                    onClick={() =>
                        logout().then(() => {
                            alert('Logged out');
                        })
                    }
                >
                    <img alt='logout' src={logoutIcon} style={{ height: '20px', width: '20px', filter: 'invert(1) brightness(2)' }} />
                </button>
            </div>
        </nav>
    );
}
